import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { aiInterviewsAPI } from '../../../lib/aiInterviewApi';
import { Empty, LANGS, Loading, Pill, PROCTORING } from './common';

const PLAN_TONE = { ready: 'green', generating: 'teal', failed: 'red' };
const PLAN_LABEL = { ready: 'Ready', generating: 'Drafting competencies…', failed: 'Generation failed' };

const PlanCard = ({ p, onInvite }) => {
  const navigate = useNavigate();
  const total = Object.values(p.counts || {}).reduce((a, b) => a + b, 0);
  return (
    <div className="iv-card" style={{ marginBottom: 0 }} data-testid={`plan-card-${p.id}`}>
      <div className="iv-row" style={{ justifyContent: 'space-between' }}>
        <b style={{ color: '#002A38' }}>{p.name}</b>
        <Pill tone={PLAN_TONE[p.status]}>{PLAN_LABEL[p.status] || p.status}</Pill>
      </div>
      <div className="iv-small" style={{ marginTop: '0.35rem' }}>
        {p.duration_min} min · {LANGS[p.language] || p.language} · {PROCTORING[p.proctoring_level]} proctoring · {p.competencies.length} competencies
      </div>
      <div className="iv-small" style={{ marginTop: '0.2rem' }}>
        {total} interview{total === 1 ? '' : 's'}{p.counts?.report_ready ? ` · ${p.counts.report_ready} report${p.counts.report_ready === 1 ? '' : 's'} ready` : ''}
        {p.auto_invite_on_stage ? ' · auto-invites at Interview stage' : ''}
      </div>
      <div className="iv-row" style={{ marginTop: '0.8rem' }}>
        <button type="button" className="iv-btn iv-btn-ghost iv-btn-sm" onClick={() => navigate(`/ai-interviews/plans/${p.id}`)} data-testid={`plan-edit-btn-${p.id}`}>Edit plan</button>
        <button type="button" className="iv-btn iv-btn-primary iv-btn-sm" disabled={p.status !== 'ready'} onClick={() => onInvite(p)} data-testid={`plan-invite-btn-${p.id}`}>Invite</button>
      </div>
    </div>
  );
};

export default function PlansTab({ refreshKey, onInvite, onCreate }) {
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    let t;
    const load = () => aiInterviewsAPI.plans().then(({ data }) => {
      setPlans(data);
      if (data.some((p) => p.status === 'generating')) t = setTimeout(load, 4000);
    }).catch(() => { toast.error('Could not load plans'); setPlans([]); });
    load();
    return () => clearTimeout(t);
  }, [refreshKey]);

  if (!plans) return <Loading />;
  if (!plans.length) {
    return (
      <Empty testId="plans-empty" title="No interview plans yet" body="A plan sets the duration, language, proctoring level and the competencies every candidate for a role is assessed on.">
        <button type="button" className="iv-btn iv-btn-cta" onClick={onCreate} data-testid="plans-empty-create-btn">Create Interview Plan</button>
      </Empty>
    );
  }
  const byJob = plans.reduce((m, p) => ({ ...m, [p.job_title || 'Untitled job']: [...(m[p.job_title || 'Untitled job'] || []), p] }), {});
  return (
    <div data-testid="plans-tab" style={{ display: 'grid', gap: '1.25rem' }}>
      {Object.entries(byJob).map(([job, list]) => (
        <div key={job}>
          <div className="iv-eyebrow" style={{ marginBottom: '0.5rem' }}>{job}</div>
          <div style={{ display: 'grid', gap: '0.75rem', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))' }}>
            {list.map((p) => <PlanCard key={p.id} p={p} onInvite={onInvite} />)}
          </div>
        </div>
      ))}
    </div>
  );
}
